import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Activity,
  Inbox,
  TrendingUp,
  LineChart,
  BarChart3,
  PieChart,
  Newspaper,
  Settings as SettingsIcon,
  ScrollText,
  Search,
  BookOpen,
} from "lucide-react";
import { Dialog } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { useGetSettingsQuery } from "@/store/api";

interface Command {
  to: string;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  /** When set, the description gets a "(DISABLED)" / "(PAUSED)" suffix
   *  if the corresponding settings flag is off/paused. */
  enabledKey?: string;
  pausedKey?: string;
}

const COMMANDS: Command[] = [
  { to: "/", label: "Overview", description: "KPIs, equity curve, status", icon: LayoutDashboard },
  { to: "/cycles", label: "Cycles", description: "Hourly bot decisions + step trace", icon: Activity },
  { to: "/pending", label: "Pending", description: "Approval queue", icon: Inbox },
  { to: "/trades", label: "Trades", description: "Trade history + CSV export", icon: TrendingUp },
  { to: "/markets", label: "Markets", description: "Per-symbol stats", icon: LineChart },
  { to: "/performance", label: "Performance", description: "Equity curve, R-distribution, heatmap", icon: BarChart3 },
  { to: "/portfolio", label: "Portfolio", description: "MPT target weights + rebalance dry-run", icon: PieChart },
  { to: "/intelligence", label: "Intelligence", description: "Net flows, sentiment, lead-lag", icon: Newspaper },
  { to: "/settings", label: "Settings", description: "Risk, engines, presets", icon: SettingsIcon },
  { to: "/logs", label: "Logs", description: "Tail of logs/bot.log with level filter", icon: ScrollText },
  // Engine pages — get DISABLED hint suffix when their flag is off.
  { to: "/admin/strategy", label: "Strategy library", description: "Curated playbook + library engine",
    icon: BookOpen, enabledKey: "library_engine_enabled" },
  { to: "/admin/pre-pump", label: "Pre-pump trades", description: "Opportunity / breakout / pre-pump auto",
    icon: TrendingUp, enabledKey: "pre_pump_auto_trader_enabled" },
  { to: "/grid", label: "Grid trader", description: "Range-bound grid (BTC/BNB/SOL)",
    icon: LineChart, enabledKey: "grid_trader_enabled" },
];

interface Props {
  open: boolean;
  onClose: () => void;
}

export function CommandPalette({ open, onClose }: Props) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  // Pull settings so engine commands can show DISABLED/PAUSED hint.
  // Skip when palette is closed to avoid the fetch entirely.
  const settings = useGetSettingsQuery(undefined, { skip: !open });
  const sData: any = settings.data ?? {};

  /** Append (DISABLED) / (PAUSED) suffix when the engine is off. */
  function describeCommand(c: Command): string {
    if (!c.enabledKey && !c.pausedKey) return c.description;
    const enabled = c.enabledKey ? sData[c.enabledKey] === true : true;
    const paused = c.pausedKey ? sData[c.pausedKey] === true : false;
    if (!enabled) return `${c.description} · (DISABLED)`;
    if (paused) return `${c.description} · (PAUSED)`;
    return c.description;
  }

  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIndex(0);
      // focus on next tick so the portal has mounted
      setTimeout(() => inputRef.current?.focus(), 10);
    }
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return COMMANDS;
    return COMMANDS.filter(
      (c) =>
        c.label.toLowerCase().includes(q) || c.description.toLowerCase().includes(q),
    );
  }, [query]);

  useEffect(() => {
    if (activeIndex >= filtered.length) setActiveIndex(0);
  }, [filtered, activeIndex]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(filtered.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const cmd = filtered[activeIndex];
      if (cmd) {
        navigate(cmd.to);
        onClose();
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} size="md">
      <div className="space-y-3 -m-1">
        <div className="flex items-center gap-2 border-b border-border pb-3">
          <Search className="w-4 h-4 text-muted shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Jump to a page…"
            className="flex-1 bg-transparent outline-none text-sm placeholder:text-muted"
          />
          <span className="text-xs text-muted hidden sm:inline">Esc to close</span>
        </div>
        <ul className="max-h-72 overflow-y-auto -mx-2">
          {filtered.length === 0 && (
            <li className="px-3 py-2 text-sm text-muted">No matches.</li>
          )}
          {filtered.map((c, i) => {
            const Icon = c.icon;
            const active = i === activeIndex;
            return (
              <li key={c.to}>
                <button
                  onMouseEnter={() => setActiveIndex(i)}
                  onClick={() => {
                    navigate(c.to);
                    onClose();
                  }}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2 rounded-md text-left",
                    active ? "bg-bg" : "hover:bg-bg",
                  )}
                >
                  <Icon className="w-4 h-4 text-muted shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{c.label}</div>
                    <div className="text-xs text-muted truncate">{describeCommand(c)}</div>
                  </div>
                  <span className="ml-auto text-xs text-muted font-mono">{c.to}</span>
                </button>
              </li>
            );
          })}
        </ul>
        <div className="border-t border-border pt-2 text-xs text-muted flex items-center gap-3">
          <kbd className="px-1.5 py-0.5 rounded bg-bg border border-border">↑↓</kbd> to navigate
          <kbd className="px-1.5 py-0.5 rounded bg-bg border border-border">↵</kbd> to open
          <span className="ml-auto">
            <kbd className="px-1.5 py-0.5 rounded bg-bg border border-border">?</kbd> for shortcuts
          </span>
        </div>
      </div>
    </Dialog>
  );
}
